import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import {
  Crown,
  Check,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Star,
  Zap,
  BookOpen,
  Clock,
  BarChart3,
  Lock,
  CreditCard,
  Sparkles
} from "lucide-react";

const Premium = () => {
  const navigate = useNavigate();
  const { user, token, login } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activating, setActivating] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe");

  const sessionId = searchParams.get("session_id");
  const planId = searchParams.get("plan");
  const method = searchParams.get("method");
  const paypalToken = searchParams.get("token");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/premium/plans`).then((res) => setPlans(res.data));
  }, []);

  // Handle return from Stripe/PayPal
  useEffect(() => {
    if (!planId || (!sessionId && !paypalToken)) return;
    const sid = sessionId || paypalToken;
    setActivating(true);
    axios.post(`${API}/premium/activate/${sid}?plan_id=${planId}`, {}, { headers })
      .then(() => {
        toast.success("Premium activé avec succès !");
        // Refresh user data
        axios.get(`${API}/auth/me`, { headers }).then(() => {
          window.location.href = "/dashboard";
        });
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Erreur d'activation");
        setActivating(false);
      });
  }, [sessionId, paypalToken, planId]);

  const handleTrial = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/premium/trial`, {}, { headers });
      toast.success("Essai gratuit de 7 jours activé !");
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      setLoading(false);
    }
  };

  const handleCheckout = async (planIdSelected) => {
    setLoading(true);
    const body = { plan_id: planIdSelected, origin_url: window.location.origin };
    try {
      if (paymentMethod === "stripe") {
        const res = await axios.post(`${API}/premium/checkout/stripe`, body, { headers });
        window.location.href = res.data.url;
      } else {
        const res = await axios.post(`${API}/premium/checkout/paypal`, body, { headers });
        window.location.href = res.data.approve_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de paiement");
      setLoading(false);
    }
  };

  if (activating) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="max-w-lg mx-auto px-4 py-16 text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-amber-500 mx-auto" />
          <p className="text-lg text-slate-600">Activation de votre abonnement Premium...</p>
        </main>
      </div>
    );
  }

  if (user?.premium) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="max-w-lg mx-auto px-4 py-16 text-center">
          <Card>
            <CardContent className="p-8 space-y-4">
              <div className="h-20 w-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                <Crown className="h-10 w-10 text-amber-500" />
              </div>
              <h2 className="text-2xl font-serif font-bold text-slate-900">Vous êtes Premium !</h2>
              <p className="text-slate-600">
                {user.premium_type === "lifetime" ? "Accès à vie" :
                 user.premium_type === "trial" ? `Essai gratuit jusqu'au ${new Date(user.premium_until).toLocaleDateString("fr-FR")}` :
                 `Abonnement actif jusqu'au ${new Date(user.premium_until).toLocaleDateString("fr-FR")}`}
              </p>
              <Button onClick={() => navigate("/dashboard")} className="bg-blue-900 hover:bg-blue-800">
                Retour au tableau de bord
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const freeFeatures = [
    "3 catégories de quiz (Histoire, Valeurs, Symboles)",
    "1 examen blanc par jour",
    "10 fiches de révision",
    "Statistiques basiques",
  ];
  const premiumFeatures = [
    "Toutes les 134 questions officielles",
    "10 catégories de quiz complètes",
    "Examens blancs illimités",
    "30 fiches de révision complètes",
    "Statistiques détaillées de progression",
    "Accès prioritaire aux nouveautés",
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="premium-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-slate-600">
          <ArrowLeft className="h-4 w-4 mr-2" />Retour
        </Button>

        {/* Hero */}
        <div className="text-center mb-10">
          <div className="h-16 w-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Crown className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-2">
            Passez à Premium
          </h1>
          <p className="text-slate-600 max-w-lg mx-auto">
            Débloquez toutes les fonctionnalités et maximisez vos chances de réussite au test civique.
          </p>
        </div>

        {/* Trial Banner */}
        {!user?.trial_used && (
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-xl p-5 mb-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="trial-banner">
            <div className="flex items-center gap-3">
              <Sparkles className="h-6 w-6 shrink-0" />
              <div>
                <p className="font-semibold">Essai gratuit de 7 jours</p>
                <p className="text-sm text-amber-100">Testez toutes les fonctionnalités Premium sans engagement</p>
              </div>
            </div>
            <Button
              onClick={handleTrial}
              disabled={loading}
              className="bg-white text-amber-600 hover:bg-amber-50 shrink-0 font-semibold"
              data-testid="trial-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
              Essayer gratuitement
            </Button>
          </div>
        )}

        {/* Comparison */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Free */}
          <Card className="border-slate-200">
            <CardHeader className="text-center pb-2">
              <p className="text-sm text-slate-500 uppercase tracking-wide">Gratuit</p>
              <CardTitle className="text-3xl font-bold">0€</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {freeFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Premium */}
          <Card className="border-2 border-amber-400 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">POPULAIRE</div>
            <CardHeader className="text-center pb-2">
              <p className="text-sm text-amber-600 uppercase tracking-wide font-semibold flex items-center justify-center gap-1">
                <Crown className="h-4 w-4" /> Premium
              </p>
              <div className="flex items-center justify-center gap-4 mt-2">
                <div className="text-center">
                  <CardTitle className="text-3xl font-bold">4.99€</CardTitle>
                  <p className="text-xs text-slate-500">/mois</p>
                </div>
                <span className="text-slate-300">ou</span>
                <div className="text-center">
                  <CardTitle className="text-3xl font-bold">9.99€</CardTitle>
                  <p className="text-xs text-slate-500">à vie</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {premiumFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-900 font-medium">
                    <Check className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Payment Method */}
        <div className="flex gap-3 mb-4 max-w-md mx-auto">
          <button onClick={() => setPaymentMethod("stripe")}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-medium transition-all ${paymentMethod === "stripe" ? "border-blue-900 bg-blue-50 text-blue-900" : "border-slate-200 bg-white text-slate-500"}`}
            data-testid="pm-stripe">
            <CreditCard className="h-4 w-4" />Carte bancaire
          </button>
          <button onClick={() => setPaymentMethod("paypal")}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-medium transition-all ${paymentMethod === "paypal" ? "border-[#0070ba] bg-blue-50 text-[#0070ba]" : "border-slate-200 bg-white text-slate-500"}`}
            data-testid="pm-paypal">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/></svg>
            PayPal
          </button>
        </div>

        {/* Purchase Buttons */}
        <div className="grid sm:grid-cols-2 gap-4 max-w-md mx-auto">
          <Button onClick={() => handleCheckout("monthly")} disabled={loading}
            className="bg-blue-900 hover:bg-blue-800 h-12" data-testid="buy-monthly">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Star className="h-4 w-4 mr-2" />}
            Mensuel — 4.99€
          </Button>
          <Button onClick={() => handleCheckout("lifetime")} disabled={loading}
            className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 h-12" data-testid="buy-lifetime">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
            À vie — 9.99€
          </Button>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4">
          Paiement sécurisé. Satisfait ou remboursé sous 14 jours.
        </p>
      </main>
    </div>
  );
};

export default Premium;
